import IdeyaTypography from '../../common/antd/ideya-typography.tsx'
import { convertPxToNumber, isV2Ready } from '../../utils/helpers.ts'
import { useAppSelector } from '../../redux/hooks.ts'
import { selectThemeStyle } from '../../features/setting/setting.slice.ts'
import { useMemo, useRef, useState } from 'react'
import { OverallRankingUserControls } from './components/overall-ranking-user-controls.tsx'
import { useListRankingQuery } from '../../apis/ranking/ranking-api.api.ts'
import { selectRankingInput } from '../../redux/slices/ranking-input.slice.ts'
import { OverallRankingTable } from './components/overall-ranking-table/overall-ranking-table.tsx'
import { OverallRankingChart } from './components/overall-ranking-chart/overall-ranking-chart.tsx'
import { OverallRankingCard } from '@/pages/overall-ranking/components/overall-ranking-card/overall-ranking-card.tsx'
import { OverallRankingHighlight } from '@/pages/overall-ranking/components/overall-ranking-card/overall-ranking-highlight.tsx'
import { useIsMobile } from '../../common/hooks/use-is-mobile.ts'
import Col from 'antd/es/grid/col'
import Row from 'antd/es/grid/row'
import Switch from 'antd/es/switch/index'
import { RankDetail } from '../../apis/ranking/ranking-api.def.ts'
import { useTableHeight } from '../../common/hooks/use-table-height.ts'

// Sort by rank, nulls last
const sortByRank = (list: RankDetail[]): RankDetail[] =>
  list.slice().sort((a, b) => {
    const ar = a?.rank
    const br = b?.rank
    if (ar == null && br == null) return 0
    if (ar == null) return 1
    if (br == null) return -1
    return ar - br
  })

export const OverallRanking = () => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const [showRankingBox, setShowRankingBox] = useState<boolean>(false)
  const rankingInput = useAppSelector(selectRankingInput)
  const [searchFullName, setSearchFullName] = useState<string>('')
  const isMobile = useIsMobile(768)
  const headerRef = useRef<HTMLDivElement>(null)
  const tableHeight = useTableHeight(headerRef)

  const processedRegion = useMemo(() => {
    let region = rankingInput.region ?? ''
    let netCategory = ''

    // Parse region if it contains " - " separator
    if (rankingInput.region && rankingInput.region.includes(' - ')) {
      const parts = rankingInput.region.split(' - ')
      region = parts[0] // "Greater China"
      netCategory = parts[1] // "Low Net"
    }

    return { region, netCategory }
  }, [rankingInput.region])

  const { data, isFetching, isLoading, refetch } = useListRankingQuery({
    startDate: rankingInput.dateRange[0],
    endDate: rankingInput.dateRange[1],
    region: processedRegion.region,
    netCategory: processedRegion.netCategory,
    firmId: rankingInput.firm,
    groupByFirm: rankingInput.groupByFirm,
  })

  // The top 3 always reflect the whole region, so this query drops the firm
  // filter. With no firm selected the args match the query above and RTK Query
  // serves both from one request.
  const {
    data: topRankData,
    isFetching: isTopRankFetching,
    isLoading: isTopRankLoading,
  } = useListRankingQuery({
    startDate: rankingInput.dateRange[0],
    endDate: rankingInput.dateRange[1],
    region: processedRegion.region,
    netCategory: processedRegion.netCategory,
    firmId: '',
    groupByFirm: rankingInput.groupByFirm,
  })

  const topRankingList = useMemo(
    () =>
      sortByRank(Object.values(topRankData?.data?.rankingsGroup ?? {}).flat()),
    [topRankData?.data?.rankingsGroup]
  )

  const filteredRankingList = useMemo(() => {
    const groups = data?.data?.rankingsGroup ?? {}
    let list = sortByRank(Object.values(groups).flat())

    // Filter by searchFullName if provided
    if (searchFullName) {
      if (rankingInput.groupByFirm) {
        list = list.filter((item) =>
          item.firmName.toLowerCase().includes(searchFullName.toLowerCase())
        )
      } else {
        list = list.filter((item) =>
          item.bookName.toLowerCase().includes(searchFullName.toLowerCase())
        )
      }
    }

    return list
  }, [data?.data?.rankingsGroup, searchFullName])

  return (
    <>
      {isV2Ready && (
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginXS),
            convertPxToNumber(themeStyle.marginXS),
          ]}
          style={{
            marginBottom: themeStyle.marginSM,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          <Col style={isMobile ? { marginBottom: 8 } : {}}>
            <IdeyaTypography.Text>Show Ranking</IdeyaTypography.Text>
          </Col>
          <Col>
            <Switch checked={showRankingBox} onChange={setShowRankingBox} />
          </Col>
        </Row>
      )}
      <Row
        gutter={[
          convertPxToNumber(themeStyle.marginLG),
          convertPxToNumber(themeStyle.marginLG),
        ]}
        style={{
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {showRankingBox && (
          <Col
            span={isMobile ? 24 : 6}
            style={isMobile ? { marginBottom: 16 } : {}}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${themeStyle.sumCardOutline}`,
                backgroundColor: themeStyle.sumCardBg,
              }}
            >
              <OverallRankingChart
                type={'Individual'}
                topPnl={10000}
                userPnl={5000}
              />
              <OverallRankingChart
                type={'Company'}
                topPnl={20000}
                userPnl={1000}
              />
            </div>
          </Col>
        )}
        <Col span={isMobile ? 24 : showRankingBox ? 18 : 24}>
          <div ref={headerRef}>
            <OverallRankingUserControls
              refetchRanking={refetch}
              searchFullName={searchFullName}
              setSearchFullName={setSearchFullName}
            />
            {!isMobile && (
              <div style={{ marginBottom: themeStyle.marginBase }}>
                <OverallRankingHighlight
                  rankingList={topRankingList}
                  groupByFirm={rankingInput.groupByFirm}
                  startDate={rankingInput.dateRange[0]}
                  endDate={rankingInput.dateRange[1]}
                />
              </div>
            )}
          </div>
          {isMobile ? (
            <OverallRankingCard
              rankingList={filteredRankingList}
              podiumList={topRankingList}
              groupByFirm={rankingInput.groupByFirm}
              startDate={rankingInput.dateRange[0]}
              endDate={rankingInput.dateRange[1]}
              isLoading={
                isLoading || isFetching || isTopRankLoading || isTopRankFetching
              }
            />
          ) : (
            <OverallRankingTable
              isLoading={isLoading}
              isFetching={isFetching}
              rankingList={filteredRankingList}
              groupByFirm={rankingInput.groupByFirm}
              height={tableHeight}
            />
          )}
        </Col>
      </Row>
    </>
  )
}
